import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useBooking } from "../BookingContext";
import { publicLocationsApi as locationsApi } from "../../api";
import { ensureMinDays } from "../locationUtils";
import { MapPin, Calendar, Search, Info } from "lucide-react";

export default function SearchBar({ compact = false }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { pickupLocation, dropoffLocation, pickupDate, dropoffDate, sameLocation, today, update } = useBooking();
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    locationsApi.list().then(setLocations).catch(() => setLocations([]));
  }, []);

  // ensure the booking has a valid pickup location once locations load
  useEffect(() => {
    if (locations.length === 0) return;
    if (!locations.find((l) => l.name === pickupLocation)) {
      const first = locations[0];
      const adjusted = ensureMinDays(pickupDate, dropoffDate, first.minBookingDays);
      update({
        pickupLocation: first.name,
        dropoffLocation: first.name,
        dropoffDate: adjusted,
      });
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedLoc = locations.find((l) => l.name === pickupLocation);
  const minDays = selectedLoc?.minBookingDays || 1;

  const setPickup = (name) => {
    const loc = locations.find((l) => l.name === name);
    const m = loc?.minBookingDays || 1;
    const adjustedDrop = ensureMinDays(pickupDate, dropoffDate, m);
    update(
      sameLocation
        ? { pickupLocation: name, dropoffLocation: name, dropoffDate: adjustedDrop }
        : { pickupLocation: name, dropoffDate: adjustedDrop }
    );
  };

  const setPickupDate = (val) => {
    const adjustedDrop = ensureMinDays(val, dropoffDate, minDays);
    update({ pickupDate: val, dropoffDate: adjustedDrop });
  };

  const onSearch = () => navigate("/cars");

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 md:p-5 ${compact ? "" : "border border-gray-100"}`}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="field-label flex items-center gap-1">
            <MapPin size={12} className="text-red-500" /> {t("search.pickup")}
          </label>
          <select
            className="input-soft"
            value={pickupLocation}
            onChange={(e) => setPickup(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
        {!sameLocation && (
          <div className="md:col-span-2">
            <label className="field-label flex items-center gap-1">
              <MapPin size={12} className="text-red-500" /> {t("search.dropoff")}
            </label>
            <select
              className="input-soft"
              value={dropoffLocation}
              onChange={(e) => update({ dropoffLocation: e.target.value })}
            >
              {locations.map((l) => (
                <option key={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="field-label flex items-center gap-1">
            <Calendar size={12} className="text-red-500" /> {t("search.pickupDate")}
          </label>
          <input
            type="date"
            className="input-soft"
            min={today}
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
          />
        </div>
        <div>
          <label className="field-label flex items-center gap-1">
            <Calendar size={12} className="text-red-500" /> {t("search.dropoffDate")}
          </label>
          <input
            type="date"
            className="input-soft"
            min={(() => {
              const d = new Date(pickupDate);
              if (isNaN(d.getTime())) return pickupDate;
              d.setDate(d.getDate() + minDays);
              return d.toISOString().slice(0, 10);
            })()}
            value={dropoffDate}
            onChange={(e) => update({ dropoffDate: e.target.value })}
          />
        </div>
        {sameLocation && <div className="hidden md:block" />}
        <button
          onClick={onSearch}
          className="pill-btn primary w-full inline-flex items-center justify-center gap-2"
          style={{ padding: "0.85rem 1rem" }}
        >
          <Search size={16} /> {t("search.search")}
        </button>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={sameLocation}
            onChange={(e) =>
              update({
                sameLocation: e.target.checked,
                ...(e.target.checked ? { dropoffLocation: pickupLocation } : {}),
              })
            }
          />
          {t("search.sameLocation")}
        </label>
        {selectedLoc && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Info size={12} className="text-red-500" />
            Minimum booking at <span className="font-semibold text-gray-700 mx-1">{selectedLoc.name}</span> is
            <span className="font-bold text-red-600 ml-1">
              {minDays} day{minDays === 1 ? "" : "s"}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
